import { vec3 } from "gl-matrix";

import {
  ActorPose,
  ActorPoseCompressed,
  ActorPoseWireLegacy
} from "pik-solver/src/apps/mannequin/types/actor-pose";

import {
  TrackedEvent,
  TrackedEventSerializer
} from "../../raw/tracked-event";
import {
  GameEvent,
  GameEventSerializer
} from "../game-event";

import {
  TrackingBatPosition,
  TrackingBatPositionSerializer
} from "./bat";
import { SkeletalPosition } from "../../raw";
import {
  TrackingFrameWire
} from "../../../flatbuffers/mannequin";
import { Builder } from "flatbuffers";
import {
  vec3FromFlatBuffer,
  vec3ToFlatBuffer
} from "../../../../util/gl-matrix/misc";
import { SkeletalPositionSerializer } from "../../raw/skeletal";
import { CompressedActorPoseSerializer } from "./actor-pose-compressed";
import {
  BallPolynomial,
  BallPolynomialSerializer
} from "../../raw/ball-polynomial";

export interface TrackingFrameWireLegacy {
  actorPoses: ActorPoseWireLegacy[];
  ballPosition?: vec3;

  gameEvents?: GameEvent[];
  trackedEvents?: TrackedEvent[];
  inferredBat?: TrackingBatPosition;

  // DEBUG remove??
  rawJoints?: SkeletalPosition[];

  num: number;
  time: number; // epoch ms utc
  timestamp: string;
}

export interface TrackingFrame {
  actorPoses: (ActorPoseCompressed | ActorPose)[];
  ballPosition?: vec3;

  gameEvents?: GameEvent[];
  trackedEvents?: TrackedEvent[];
  inferredBat?: TrackingBatPosition;
  ballPolynomials?: BallPolynomial[];

  rawJoints?: SkeletalPosition[];

  num: number;
  time: number; // epoch ms utc
  timestamp: string;
}

export class TrackingFrameSerializer {
  static serializeBinary(
    frame: TrackingFrame,
    builder: Builder
  ): number | undefined {
    const poseOffsets = frame.actorPoses
      .filter(p => !(p instanceof ActorPose))
      .map(p => CompressedActorPoseSerializer.serializeBinary(p as ActorPoseCompressed, builder))
      .filter((offset): offset is number => typeof offset === "number");

    if (poseOffsets.length !== frame.actorPoses.length) {
      console.error("failure to properly serialise actor poses");
      return undefined;
    }

    const actorPoses = TrackingFrameWire.createActorPosesVector(
      builder, poseOffsets
    );

    const gameEventOffsets = frame.gameEvents
      ?.map(e => GameEventSerializer.serializeBinary(e, builder))
      .filter((offset): offset is number => typeof offset === "number");

    if (
      gameEventOffsets &&
      (gameEventOffsets.length !== frame.gameEvents?.length)
    ) {
      console.error("failure to properly serialise game events");
      return undefined;
    }
    const gameEvents = gameEventOffsets
      ? TrackingFrameWire.createGameEventsVector(builder, gameEventOffsets)
      : undefined;

    const trackedEventOffsets = frame.trackedEvents
      ?.map(e => TrackedEventSerializer.serializeBinary(e, builder))
      .filter((offset): offset is number => typeof offset === "number");

    if (
      trackedEventOffsets &&
      (trackedEventOffsets.length !== frame.trackedEvents?.length)
    ) {
      console.error("failure to properly serialise tracked events");
      return undefined;
    }
    const trackedEvents = trackedEventOffsets
      ? TrackingFrameWire.createTrackedEventsVector(builder, trackedEventOffsets)
      : undefined;

    let ballPolynomialOffsets = frame.ballPolynomials
      ?.map(e => BallPolynomialSerializer.serializeBinary(e, builder))
      .filter((offset): offset is number => typeof offset === "number");

    if (
      ballPolynomialOffsets &&
      (ballPolynomialOffsets.length !== frame.ballPolynomials?.length)
    ) {
      console.error("failure to properly serialise ball polynomials");
      ballPolynomialOffsets = undefined;
    }
    const ballPolynomials = ballPolynomialOffsets
      ? TrackingFrameWire.createBallPolynomialsVector(builder, ballPolynomialOffsets)
      : undefined;

    const inferredBat = frame.inferredBat
      ? TrackingBatPositionSerializer.serializeBinary(frame.inferredBat, builder)
      : undefined;

    const rawJointOffsets = frame.rawJoints
      ?.map(p => SkeletalPositionSerializer.serializeBinary(p, builder))
      .filter((offset): offset is number => typeof offset === "number");
    if (
      rawJointOffsets &&
      (rawJointOffsets.length !== frame.rawJoints?.length)
    ) {
      console.error("failure to properly serialise tracked events");
      return undefined;
    }
    const rawJoints = rawJointOffsets
      ? TrackingFrameWire.createRawJointsVector(builder, rawJointOffsets)
      : undefined;

    const timestamp = builder.createString(frame.timestamp);

    TrackingFrameWire.startTrackingFrameWire(builder);

    // add to frame
    TrackingFrameWire.addActorPoses(builder, actorPoses);
    if (frame.ballPosition) {
      TrackingFrameWire.addBallPosition(builder, vec3ToFlatBuffer(frame.ballPosition, builder));
    }
    if (gameEvents) {
      TrackingFrameWire.addGameEvents(builder, gameEvents);
    }
    if (trackedEvents) {
      TrackingFrameWire.addTrackedEvents(builder, trackedEvents);
    }
    if (ballPolynomials) {
      TrackingFrameWire.addBallPolynomials(builder, ballPolynomials);
    }
    if (inferredBat) {
      TrackingFrameWire.addInferredBat(builder, inferredBat);
    }
    if (rawJoints) {
      TrackingFrameWire.addRawJoints(builder, rawJoints);
    }
    TrackingFrameWire.addNum(builder, frame.num);
    TrackingFrameWire.addTime(builder, frame.time);
    TrackingFrameWire.addTimestamp(builder, timestamp);

    return TrackingFrameWire.endTrackingFrameWire(builder);
  }

  static deserializeBinary(
    wire?: TrackingFrameWire
  ): TrackingFrame | undefined {
    if (!wire) return undefined;

    const posesUnpacked: ActorPoseCompressed[] = [];
    for (let i = 0; i < wire.actorPosesLength(); i++) {
      const pose = wire.actorPoses(i);
      if (pose) {
        const unpacked = CompressedActorPoseSerializer.deserializeBinary(pose);
        if (unpacked === undefined) {
          console.error("blank actor pose");
          return undefined;
        }
        posesUnpacked.push(unpacked);
      }
    }

    const timestamp = wire.timestamp();
    if (!timestamp) {
      console.error("invalid timestamp");
    }

    const ballPosition = wire.ballPosition();

    const frame: TrackingFrame = {
      actorPoses: posesUnpacked,
      ballPosition: ballPosition ? vec3FromFlatBuffer(ballPosition) : undefined,

      num: wire.num(),
      time: wire.time(),
      timestamp: timestamp || "error",
    };


    if (wire.gameEventsLength() > 0) {
      frame.gameEvents = [];
      for (let i = 0; i < wire.gameEventsLength(); i++) {
        const eventWire = wire.gameEvents(i);
        const event = GameEventSerializer.deserializeBinary(eventWire || undefined);
        if (event) {
          frame.gameEvents.push(event);
        } else {
          console.error("game event issue");
          return undefined;
        }
      }
    }

    if (wire.trackedEventsLength() > 0) {
      frame.trackedEvents = [];
      for (let i = 0; i < wire.trackedEventsLength(); i++) {
        const eventWire = wire.trackedEvents(i);
        const event = TrackedEventSerializer.deserializeBinary(
          eventWire || undefined
        );
        if (event) {
          frame.trackedEvents.push(event);
        } else {
          console.error("tracked event issue");
          return undefined;
        }
      }
    }

    if (wire.ballPolynomialsLength() > 0) {
      frame.ballPolynomials = [];
      for (let i = 0; i < wire.ballPolynomialsLength(); i++) {
        const polyWire = wire.ballPolynomials(i);
        const polynomial = BallPolynomialSerializer.deserializeBinary(
          polyWire || undefined
        );
        if (polynomial) {
          frame.ballPolynomials.push(polynomial);
        } else {
          console.error("ball polynomial issue");
          return undefined;
        }
      }
    }

    const bat = wire.inferredBat();
    if (bat) {
      frame.inferredBat = TrackingBatPositionSerializer.deserializeBinary(
        bat
      );
    }

    if (wire.rawJointsLength() > 0) {
      frame.rawJoints = [];
      for (let i = 0; i < wire.rawJointsLength(); i++) {
        const jointWire = wire.rawJoints(i);
        const joint = SkeletalPositionSerializer.deserializeBinary(
          jointWire || undefined
        );
        if (joint) {
          frame.rawJoints.push(joint);
        } else {
          console.error("raw joint data issue");
        }
      }
    }

    return frame;
  }

  static deserializeLegacy(
    wire: TrackingFrameWireLegacy
  ): TrackingFrame {
    const posesUnpacked: ActorPose[] = [];
    for (const pose of wire.actorPoses) {
      posesUnpacked.push(ActorPose.deserializeLegacy(pose));
    }

    return {
      actorPoses: posesUnpacked,
      ballPosition: wire.ballPosition,

      gameEvents: wire.gameEvents,
      trackedEvents: wire.trackedEvents,
      inferredBat: wire.inferredBat,

      rawJoints: wire.rawJoints,

      num: wire.num,
      time: wire.time,
      timestamp: wire.timestamp,
    }
  }
}
